import { useEffect, useState } from "react";
import { Badge, Card, CardBody, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Row, Spinner } from "reactstrap";
import {
  IoWalletOutline,
  IoReceiptOutline,
  IoTimeOutline,
  IoCardOutline,
  IoAddOutline,
  IoListOutline,
  IoGridOutline,
  IoCogOutline,
  IoBarChartOutline,
  IoCalendarOutline,
} from "react-icons/io5";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { endOfMonth, endOfYear, format, startOfMonth, startOfYear, subMonths, subYears } from "date-fns";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import { get } from "../../../../utils/helpers/api";
import { useSettings } from "../../../../context/settings";
import "./styles.css";
import StatCard from "../../../../components/Cards/StatCard";

const DATE_RANGE_OPTIONS = ["this_month", "last_month", "this_year", "last_year", "custom"];

function getDateRange(option, customStart, customEnd) {
  const now = new Date();
  switch (option) {
    case "last_month": {
      const d = subMonths(now, 1);
      return [startOfMonth(d), endOfMonth(d)];
    }
    case "this_year":
      return [startOfYear(now), endOfYear(now)];
    case "last_year": {
      const d = subYears(now, 1);
      return [startOfYear(d), endOfYear(d)];
    }
    case "custom":
      return [customStart, customEnd];
    case "this_month":
    default:
      return [startOfMonth(now), endOfMonth(now)];
  }
}

const CATEGORY_COLORS = [
  "#6C63FF", "#20c997", "#f76707", "#4dabf7", "#fa5252",
  "#ffd43b", "#ae3ec9", "#2f9e44", "#e64980", "#0c8599",
];

const STATUS_BADGE = {
  paid:    "acc-badge-paid",
  pending: "acc-badge-pending",
  overdue: "acc-badge-overdue",
};

const tooltipStyle = {
  fontSize: "0.78rem",
  borderRadius: 8,
  border: "1px solid #eee",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
};

function EmptyState({ message }) {
  return (
    <div className="acc-empty-state">
      <IoListOutline size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
      <span>{message}</span>
    </div>
  );
}

function formatMoney(value, currency = "USD") {
  if (!value && value !== 0) return `0 ${currency}`;
  return `${Number(value).toLocaleString()} ${currency}`;
}

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { settings, formatDate, datePickerFormat } = useSettings();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [dateRangeOption, setDateRangeOption] = useState("this_month");
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [appliedRange, setAppliedRange] = useState(() => getDateRange("this_month"));
  const [kpiData, setKpiData] = useState({
    revenueThisMonth: 0,
    expensesThisMonth: 0,
    outstandingReceivables: 0,
    outstandingPayables: 0,
  });
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [expensesByCategory, setExpensesByCategory] = useState([]);
  const [recentExpenses, setRecentExpenses] = useState([]);

  const getHomeData = async (range) => {
    setLoading(true);

    const [from, to] = range;
    const params = new URLSearchParams();
    if (from) params.append("date_from", format(from, "yyyy-MM-dd"));
    if (to) params.append("date_to", format(to, "yyyy-MM-dd"));

    const data = await get(`/accounting/home?${params.toString()}`);
    if (data.success) {
      setKpiData({
        revenueThisMonth: data.revenue_this_month ?? 0,
        expensesThisMonth: data.expenses_this_month ?? 0,
        outstandingReceivables: data.outstanding_receivables ?? 0,
        outstandingPayables: data.outstanding_payables ?? 0,
      });
      setMonthlyTrend(data.monthly_trend ?? []);
      setExpensesByCategory(data.expenses_by_category ?? []);
      setRecentExpenses(data.recent_expenses ?? []);
    }
    setLoading(false);
  };

  const applyRange = (range) => {
    setAppliedRange(range);
    const [from, to] = range;
    if (from && to) {
      setSearchParams({
        start_date: format(from, "yyyy-MM-dd"),
        end_date: format(to, "yyyy-MM-dd"),
      });
    }
  };

  const selectDateRangeOption = (option) => {
    setDateRangeOption(option);
    if (option === "custom") return;
    applyRange(getDateRange(option));
    setDateDropdownOpen(false);
  };

  const applyCustomRange = () => {
    if (!startDate || !endDate) return;
    applyRange([startDate, endDate]);
    setDateDropdownOpen(false);
  };

  const dateRangeLabel = dateRangeOption === "custom"
    ? (appliedRange[0] && appliedRange[1] ? `${formatDate(appliedRange[0])} - ${formatDate(appliedRange[1])}` : t("common.custom"))
    : t(`common.${dateRangeOption.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`);

  useEffect(() => {
    getHomeData(appliedRange);
  }, [appliedRange]);

  const totalCategoryAmount = expensesByCategory.reduce((s, c) => s + (c.amount ?? 0), 0);

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t("accounting.title")} onReload={() => getHomeData(appliedRange)}>
          <Dropdown isOpen={dateDropdownOpen} toggle={() => setDateDropdownOpen(o => !o)} size="sm">
            <DropdownToggle outline color="light" caret>
              <IoCalendarOutline className="me-1" />
              {dateRangeLabel}
            </DropdownToggle>
            <DropdownMenu end>
              {DATE_RANGE_OPTIONS.filter(o => o !== "custom").map(option => (
                <DropdownItem
                  key={option}
                  active={dateRangeOption === option}
                  onClick={() => selectDateRangeOption(option)}
                >
                  {t(`common.${option.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`)}
                </DropdownItem>
              ))}
              <DropdownItem divider />
              <DropdownItem
                active={dateRangeOption === "custom"}
                toggle={false}
                onClick={() => setDateRangeOption("custom")}
              >
                {t('common.custom')}
              </DropdownItem>
              {dateRangeOption === "custom" && (
                <div className="px-3 py-2 d-flex flex-column gap-2" style={{ minWidth: 220 }}>
                  <div>
                    <div className="text-muted mb-1" style={{ fontSize: "0.72rem" }}>{t('common.from')}</div>
                    <DatePicker
                      selected={startDate}
                      onChange={setStartDate}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      maxDate={endDate || new Date()}
                      className="form-control form-control-sm"
                      dateFormat={datePickerFormat}
                      placeholderText={t('common.from')}
                    />
                  </div>
                  <div>
                    <div className="text-muted mb-1" style={{ fontSize: "0.72rem" }}>{t('common.to')}</div>
                    <DatePicker
                      selected={endDate}
                      onChange={setEndDate}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      maxDate={new Date()}
                      className="form-control form-control-sm"
                      dateFormat={datePickerFormat}
                      placeholderText={t('common.to')}
                    />
                  </div>
                  <DefaultButton size="sm" disabled={!startDate || !endDate} onClick={applyCustomRange}>
                    {t('common.apply')}
                  </DefaultButton>
                </div>
              )}
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        {loading ?
          <Spinner color="primary" className="d-block mx-auto my-4" />
        :
          <div className="subheader-page-content scrollable">
            {/* ── Row 1: KPI Cards ── */}
            <Row className="g-3 mb-4">
              <Col md="3">
                <StatCard
                  icon={<IoWalletOutline size={18} color="#6C63FF" />}
                  iconBg="#f0eeff"
                  value={formatMoney(kpiData.revenueThisMonth, settings?.sales?.base_currency)}
                  label={t("crm.revenue")}
                />
              </Col>
              <Col md="3">
                <StatCard
                  icon={<IoReceiptOutline size={18} color="#fa5252" />}
                  iconBg="#fff0f0"
                  value={formatMoney(kpiData.expensesThisMonth, settings?.sales?.base_currency)}
                  label={t("purchases.expenses")}
                />
              </Col>
              <Col md="3">
                <StatCard
                  icon={<IoTimeOutline size={18} color="#f76707" />}
                  iconBg="#fff4e6"
                  value={formatMoney(kpiData.outstandingReceivables, settings?.sales?.base_currency)}
                  label={t("accounting.outstandingReceivables")}
                />
              </Col>
              <Col md="3">
                <StatCard
                  icon={<IoCardOutline size={18} color="#4dabf7" />}
                  iconBg="#e8f4ff"
                  value={formatMoney(kpiData.outstandingPayables, settings?.sales?.base_currency)}
                  label={t("accounting.outstandingPayables")}
                />
              </Col>
            </Row>

            {/* ── Row 2: Income vs Expenses Chart ── */}
            <Row className="g-3 mb-4" style={{ alignItems: "stretch" }}>
              <Col lg="12" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t("accounting.incomeVsExpenses")}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="d-flex flex-column">
                    <div style={{ flex: 1, minHeight: 250 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={monthlyTrend}
                          margin={{ right: 8, left: -40, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis
                            dataKey="month"
                            tick={{ fontSize: 11, fill: "#555" }}
                            axisLine={false}
                            tickLine={false}
                            reversed={isRTL}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: "#555" }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(v) => `${v} ${settings?.sales?.base_currency}`}
                            width={80}
                          />
                          <Tooltip
                            contentStyle={tooltipStyle}
                            formatter={(value, name) => [
                              formatMoney(value, settings?.sales?.base_currency),
                              name === "revenue" ? t("accounting.revenueThisMonth") : t("accounting.expensesThisMonth"),
                            ]}
                            cursor={{ fill: "#f5f3ff" }}
                          />
                          <Bar dataKey="revenue" fill="#6C63FF" radius={[4, 4, 0, 0]} maxBarSize={32} />
                          <Bar dataKey="expenses" fill="#fa5252" radius={[4, 4, 0, 0]} maxBarSize={32} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="d-flex gap-4 mt-3">
                      <span style={{ fontSize: "0.78rem", color: "#555" }}>
                        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: "#6C63FF", marginRight: 5 }} />
                        {t("accounting.revenueThisMonth")}
                      </span>
                      <span style={{ fontSize: "0.78rem", color: "#555" }}>
                        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: "#fa5252", marginRight: 5 }} />
                        {t("accounting.expensesThisMonth")}
                      </span>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ── Row 3: Recent Expenses + Category Donut ── */}
            <Row className="g-3" style={{ alignItems: "stretch" }}>
              <Col lg="7" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t("accounting.recentExpenses")}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="p-0 acc-panel-body">
                    {recentExpenses.length === 0 ? (
                      <EmptyState message={t("accounting.noRecentExpenses")} />
                    ) : (
                      recentExpenses.map((expense) => (
                        <div
                          key={expense.id}
                          className="acc-panel-item d-flex align-items-center gap-3 px-3 py-2"
                        >
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="acc-panel-item-name fw-semibold text-truncate">
                              {expense.vendor || expense.description || "—"}
                            </div>
                            <div className="acc-panel-item-meta text-muted d-flex align-items-center gap-2 mt-1">
                              <span>{expense.category}</span>
                              {expense.date && (
                                <>
                                  <span>·</span>
                                  <span>{formatDate(expense.date)}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="fw-bold" style={{ fontSize: "0.88rem", whiteSpace: "nowrap" }}>
                            {formatMoney(expense.amount, settings?.sales?.base_currency)}
                          </div>
                          {expense.status && (
                            <span className={`acc-badge ${STATUS_BADGE[expense.status] ?? "acc-badge-pending"}`}>
                              {t(`common.${expense.status}`) || expense.status}
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col lg="5" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t("accounting.expensesByCategory")}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="d-flex align-items-center gap-4 h-100">
                    {expensesByCategory.length === 0 ? (
                      <EmptyState message={t("accounting.noRecentExpenses")} />
                    ) : (
                      <>
                        <PieChart width={140} height={140}>
                          <Pie
                            data={expensesByCategory}
                            dataKey="amount"
                            nameKey="category"
                            cx={70}
                            cy={70}
                            innerRadius={30}
                            outerRadius={52}
                            paddingAngle={2}
                          >
                            {expensesByCategory.map((_, i) => (
                              <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value, name) => [formatMoney(value, settings?.sales?.base_currency), name]}
                            contentStyle={tooltipStyle}
                          />
                        </PieChart>
                        <div className="acc-donut-legend flex-grow-1">
                          {expensesByCategory.map((c, i) => (
                            <div key={c.category} className="acc-donut-legend-item">
                              <div
                                className="acc-donut-dot"
                                style={{ background: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}
                              />
                              <span className="flex-grow-1 text-truncate">{c.category}</span>
                              <span className="fw-semibold">{formatMoney(c.amount, settings?.sales?.base_currency)}</span>
                              <span className="text-muted ms-2" style={{ fontSize: "0.72rem" }}>
                                {totalCategoryAmount > 0
                                  ? Math.round((c.amount / totalCategoryAmount) * 100)
                                  : 0}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        }
      </div>
    </SidebarLayout>
  );
}
